// #49 Itinerary (`itin`): the *Candidate itinerary* as a labelled grid in the body font — a <dl> of When · Format ·
// With · Where / link — with its note under it, and *Copy itinerary*: an IconButton that copies the plain text held in
// a hidden, read-only textarea (the copy source; it is never focusable or read). G8's MeetingEvent (#98) extends it.
// `copyInWords` (B13 DC-241: no control on a scheduling card is an icon alone) draws the copy control as the glossary's
// ghost *Candidate itinerary* button with the copy icon, which then carries the words the heading would repeat.

import type { ReactNode } from "react";
import { Button, copyText, Icon, IconButton } from "../core";
import { cx } from "../lib/cx";
import { ITINERARY_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

export type ItineraryItem = readonly [term: string, value: ReactNode];

export interface ItineraryProps {
  /** The grid's terms and values (When · Format · With · Where / link, then any further rows). */
  items: readonly ItineraryItem[];
  /** The heading (default *Candidate itinerary*). */
  title?: ReactNode | undefined;
  /** The line under the grid. */
  note?: ReactNode | undefined;
  /** The plain text *Copy itinerary* copies; default "term: value" lines from the string values. */
  text?: string | undefined;
  onCopy?: ((text: string) => void) | undefined;
  /** false: no copy action (the grid and note stay). */
  canCopy?: boolean | undefined;
  /** The copy control in words: a ghost *Candidate itinerary* button with the copy icon (DC-241), not an IconButton. */
  copyInWords?: boolean | undefined;
  can?: boolean | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

/** "When: Oct 2, 2026 · 10:00 AM EDT" lines, from the items whose values are text. */
export function itineraryText(items: readonly ItineraryItem[]): string {
  return items
    .filter((item): item is readonly [string, string | number] => typeof item[1] === "string" || typeof item[1] === "number")
    .map(([term, value]) => `${term}: ${value}`)
    .join("\n");
}

export function Itinerary({ items, title = ITINERARY_LABELS.candidateItinerary, note, text, onCopy, canCopy, copyInWords, can, hs, className }: ItineraryProps) {
  if (can === false) return null;
  const source = text ?? itineraryText(items);
  const copy = () => { void copyText(source).then((done) => { if (done) onCopy?.(source); }); };
  // in words, the button says what the default heading would: the heading is not drawn twice
  const worded = copyInWords === true && canCopy !== false;
  return (
    <div className={cx("mc-itin", className)}>
      <div className="mc-it-h">
        <Icon name="send" size={16} />
        {worded && title === ITINERARY_LABELS.candidateItinerary ? null : <b>{title}</b>}
        {worded ? (
          <Button sm tone="ghost" icon="copy" onClick={copy} hs={hs} className="mc-it-copy">{ITINERARY_LABELS.copyItinerary}</Button>
        ) : (
          <IconButton sm icon="copy" label={ITINERARY_LABELS.copyItinerary} onClick={copy} can={canCopy} hs={hs} className="mc-it-copy" />
        )}
      </div>
      <dl className="mc-it-g">
        {items.map(([term, value], index) => (
          <div key={index}>
            <dt>{term}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {note !== undefined ? <div className="mc-it-n">{note}</div> : null}
      <textarea className="mc-it-src" hidden readOnly aria-hidden="true" tabIndex={-1} value={source} />
    </div>
  );
}
